/**
 * WebM / Matroska (EBML) metadata (§20).
 *
 * EBML is a tree of elements, each `[id][size][data]`, where both the id and
 * the size are variable-length integers whose first byte's leading-zero count
 * gives their length. This reads only the elements that describe the media —
 * duration, picture size, codec — and decodes nothing; playback is the
 * platform's job.
 *
 * Sizes are validated against the enclosing element and the walk is
 * depth-bounded, because an unknown-size element or a size that overruns its
 * parent is exactly what a malformed or hostile file uses to make a naive
 * reader loop or read past the buffer (§30).
 */
export interface WebmInfo {
  readonly durationSec: number;
  readonly width: number | null;
  readonly height: number | null;
  readonly videoCodec: string | null;
  readonly audioCodec: string | null;
  readonly hasVideo: boolean;
  readonly hasAudio: boolean;
}

/* Element ids, kept with their length-marker bits so they compare directly. */
const ID = {
  EBML: 0x1a45dfa3,
  Segment: 0x18538067,
  Info: 0x1549a966,
  TimecodeScale: 0x2ad7b1,
  Duration: 0x4489,
  Tracks: 0x1654ae6b,
  TrackEntry: 0xae,
  TrackType: 0x83,
  CodecID: 0x86,
  Video: 0xe0,
  PixelWidth: 0xb0,
  PixelHeight: 0xba,
} as const;

const MAX_DEPTH = 8;

interface Vint { readonly value: number; readonly length: number; readonly unknown: boolean }

/** Read a variable-length integer; `keepMarker` keeps the id's marker bits. */
function readVint(bytes: Uint8Array, pos: number, keepMarker: boolean): Vint | null {
  if (pos >= bytes.length) return null;
  const first = bytes[pos]!;
  if (first === 0) return null; // a leading zero byte means length > 8: unsupported
  let length = 1;
  let mask = 0x80;
  while (length <= 8 && (first & mask) === 0) { mask >>= 1; length += 1; }
  if (length > 8 || pos + length > bytes.length) return null;

  let value = keepMarker ? first : first & (mask - 1);
  let allOnes = (first & (mask - 1)) === (mask - 1);
  for (let i = 1; i < length; i++) {
    const b = bytes[pos + i]!;
    if (b !== 0xff) allOnes = false;
    value = value * 256 + b;
  }
  return { value, length, unknown: !keepMarker && allOnes };
}

interface Element { readonly id: number; readonly dataStart: number; readonly dataEnd: number; readonly end: number }

function* children(bytes: Uint8Array, from: number, to: number): Generator<Element> {
  let pos = from;
  while (pos < to) {
    const id = readVint(bytes, pos, true);
    if (!id) return;
    const size = readVint(bytes, pos + id.length, false);
    if (!size) return;
    const dataStart = pos + id.length + size.length;
    // An unknown-size element (all-ones) runs to the end of its parent.
    const dataEnd = size.unknown ? to : dataStart + size.value;
    if (dataEnd > to || dataEnd < dataStart) return; // overruns the parent (§30)
    yield { id: id.value, dataStart, dataEnd, end: dataEnd };
    pos = dataEnd;
  }
}

function uint(bytes: Uint8Array, start: number, end: number): number {
  let v = 0;
  for (let i = start; i < end; i++) v = v * 256 + bytes[i]!;
  return v;
}

function float(view: DataView, start: number, end: number): number {
  const len = end - start;
  if (len === 4) return view.getFloat32(start, false);
  if (len === 8) return view.getFloat64(start, false);
  if (len === 0) return 0;
  return uint(new Uint8Array(view.buffer, view.byteOffset, view.byteLength), start, end);
}

function ascii(bytes: Uint8Array, start: number, end: number): string {
  let s = '';
  for (let i = start; i < end; i++) s += String.fromCharCode(bytes[i]!);
  return s.replace(/\0+$/, '');
}

export function parseWebm(bytes: Uint8Array): WebmInfo | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // The file must open with an EBML header, or it is not Matroska/WebM.
  const first = [...children(bytes, 0, bytes.length)][0];
  if (!first || first.id !== ID.EBML) return null;

  const segment = [...children(bytes, 0, bytes.length)].find((e) => e.id === ID.Segment);
  if (!segment) return null;

  let timecodeScale = 1_000_000; // nanoseconds, the Matroska default
  let durationRaw = 0;
  let width: number | null = null;
  let height: number | null = null;
  let videoCodec: string | null = null;
  let audioCodec: string | null = null;
  let hasVideo = false;
  let hasAudio = false;

  for (const el of children(bytes, segment.dataStart, segment.dataEnd)) {
    if (el.id === ID.Info) {
      for (const info of children(bytes, el.dataStart, el.dataEnd)) {
        if (info.id === ID.TimecodeScale) timecodeScale = uint(bytes, info.dataStart, info.dataEnd);
        else if (info.id === ID.Duration) durationRaw = float(view, info.dataStart, info.dataEnd);
      }
    } else if (el.id === ID.Tracks) {
      for (const entry of children(bytes, el.dataStart, el.dataEnd)) {
        if (entry.id !== ID.TrackEntry) continue;
        readTrack(bytes, entry, (kind, codec, w, h) => {
          if (kind === 1) {
            hasVideo = true;
            if (!videoCodec) videoCodec = codec;
            if (width === null) { width = w; height = h; }
          } else if (kind === 2) {
            hasAudio = true;
            if (!audioCodec) audioCodec = codec;
          }
        });
      }
    }
  }

  return {
    durationSec: (durationRaw * timecodeScale) / 1e9,
    width, height, videoCodec, audioCodec, hasVideo, hasAudio,
  };
}

function readTrack(
  bytes: Uint8Array, entry: Element,
  emit: (kind: number, codec: string | null, w: number | null, h: number | null) => void,
  depth = 0,
): void {
  if (depth > MAX_DEPTH) return;
  let kind = 0;
  let codec: string | null = null;
  let w: number | null = null;
  let h: number | null = null;

  for (const el of children(bytes, entry.dataStart, entry.dataEnd)) {
    if (el.id === ID.TrackType) kind = uint(bytes, el.dataStart, el.dataEnd);
    else if (el.id === ID.CodecID) codec = ascii(bytes, el.dataStart, el.dataEnd);
    else if (el.id === ID.Video) {
      for (const v of children(bytes, el.dataStart, el.dataEnd)) {
        if (v.id === ID.PixelWidth) w = uint(bytes, v.dataStart, v.dataEnd);
        else if (v.id === ID.PixelHeight) h = uint(bytes, v.dataStart, v.dataEnd);
      }
    }
  }
  emit(kind, codec, w, h);
}
